<script>
  import Modal from '@components/shared/modal.svelte'
  import AssignationModal from '@components/blocked-events/assignation-modal.svelte'
  import SegmentedControl from '@components/shared/segmented-control.svelte'
  import Autocomplete from '@components/shared/autocomplete.svelte'
  import AutocompleteMultiple from '@components/shared/autocomplete-multiple.svelte'
  import Scheduler from '@components/scheduler/scheduler.svelte'
  import FormLabel from '@components/shared/form-label.svelte'
  import { get, post } from '@lib/requests'
  import { Turbo } from '@hotwired/turbo-rails'
  import { showAlert, currentUser as getCurrentUser } from '@lib/utils'
  import ClientForm from '@components/clients/form.svelte'
  import { format, getDay } from 'date-fns'
  import { onMount } from 'svelte'
  import { MediaQuery } from 'svelte/reactivity'
  
  let { immediateOpen = false, clinicId, vetId, date, startTime, endTime, clientId, animalIds, calendar = false, allDay = false } = $props()
  // Unique per instance: the footer buttons target the form by id, and more than one form can be mounted on the page
  const uid = $props.id()
  const formId = `consultation-form-${uid}`

  let open = $state(immediateOpen)
  let assignationOpen = $state(false)
  let animalOptions = $state([])
  let clientOptions = $state([])
  let clientAutocomplete = $state(null)
  let animalAutocomplete = $state(null)
  let motiveElement = $state(null)
  let notesElement = $state(null)
  let scheduler = $state(null)
  let slotUnavailable = $state(false)
  let submitting = $state(false)
  let clientFormOpen = $state(false)
  let clientAdded = $state(false)
  let blockOperation = $state(false)
  let matchMotives = $state(false)
  let categories = $state([])
  let duration = $state(10)

  let users = $state([])
  let animals = $state([])
  let activeAnimal = $state(null)

  let assistantUsers = $derived(
    users
      .filter(user => user.role === "assistant")
      .sort((a, b) => a.name.localeCompare(b.name))
  )
  let otherUsers = $derived(
    users
      .filter(user => !assistantUsers.includes(user) && (user.role === "clinic_admin" || user.vet))
      .sort((a, b) => a.name.localeCompare(b.name))
  )

  let clinics = $derived(JSON.parse(document.querySelector("[data-clinics]").dataset.clinics))
  let clinic = $derived(clinicId ? clinics.find(clinic => clinic.id === Number(clinicId)) : null)
  let currentUser = $derived(getCurrentUser())

  const formData = $state({
    clinic_id: clinicId,
    vet_id: vetId || null,
    date: date || new Date().toISOString().split('T')[0],
    start_time: startTime || null,
    end_time: endTime || null,
    client_id: clientId || null,
    animal_ids: animalIds || [],
    type: null,
    duty_shift: 0,
    nil_by_mouth: 0,
    motive: "",
    reserved_by: null,
    consultation_category_id: null
  })

  // Categories are only returned for clinics with the feature enabled, and only apply to scheduled consultations for now
  let typeCategories = $derived(categories.filter(category => category.consultation_type === formData.type))
  let showCategories = $derived(!blockOperation && typeCategories.length > 0)
  let selectedCategory = $derived(categories.find(category => category.id === Number(formData.consultation_category_id)) || null)
  // Assistants can't change the duration set by a category
  let durationLocked = $derived(Boolean(selectedCategory) && Boolean(currentUser?.assistant))

  // Required fields are checked on submit (highlighting what's missing) rather than disabling the buttons without saying why
  let missingFields = $derived.by(() => {
    const missing = []
    if (blockOperation) {
      if (!formData.vet_id || !formData.start_time || !formData.end_time) missing.push({ key: "slot", label: "Créneau" })
      if (!formData.notes) missing.push({ key: "notes", label: "Notes" })
    } else {
      if (!formData.client_id) missing.push({ key: "client", label: "Client" })
      if (!formData.type) missing.push({ key: "type", label: "Type de consultation" })
      if (!formData.vet_id) missing.push({ key: "slot", label: formData.start_time || !timed ? "Vétérinaire" : "Créneau" })
      if (!formData.reserved_by) missing.push({ key: "reserved_by", label: "Réservé par" })
    }
    return missing
  })
  // svelte-ignore state_referenced_locally
  let timed = $state(!allDay)
  let submitAttempted = $state(false)
  let invalid = $derived(new Set(submitAttempted ? missingFields.map(field => field.key) : []))
  let typeElement = $state(null)
  let reservedByElement = $state(null)

  function validate() {
    submitAttempted = true
    if (slotUnavailable && !blockOperation) {
      showAlert("Ce créneau n'est plus disponible", { type: "error" })
      return false
    }
    if (missingFields.length === 0) return true
    showAlert(`Champs requis manquants : ${missingFields.map(field => field.label).join(", ")}`, { type: "error" })
    focusField(missingFields[0].key)
    return false
  }

  function focusField(key) {
    if (key === "client") clientAutocomplete?.focus()
    else if (key === "type") typeElement?.focus()
    else if (key === "reserved_by") reservedByElement?.focus()
    else if (key === "notes") notesElement?.focus()
    else if (key === "slot") scheduler?.focusVet()
  }

  // Two columns (fields | slot grid) on wide screens
  const wideScreen = new MediaQuery("min-width: 1280px")
  let twoColumn = $derived(wideScreen.current)
  let pairSpan = $derived(twoColumn ? "col-span-12" : "col-span-6")
  // Category names are longer than type names, so the category gets more of the row in the narrower two-column layout
  let typeSpan = $derived(!showCategories ? "col-span-12" : twoColumn ? "col-span-5" : "col-span-6")
  let categorySpan = $derived(twoColumn ? "col-span-7" : "col-span-6")

  let modalTitle = $derived(blockOperation ? "Bloquer un créneau" : "Nouvelle consultation")

  async function fetchAnimals() {
    const response = await get(`/api/v1/animals?client_id=${formData.client_id}`)
    animalOptions = await response.json
    if (animalOptions.length === 1) {
      animalAutocomplete?.setValue(animalOptions, { triggerCallback: true })
    } else if (animalOptions.length > 0) {
      animalAutocomplete?.focus()
    }
  }

  function setAnimals() {
    if (!formData.animal_ids.length) return animals = []
    animals = animalOptions.filter(animal => formData.animal_ids.includes(animal.id)).map(animal => {
      return { ...animal, motive: selectedCategory?.name || "" }
    })
    activeAnimal = animals[0]
  }
  
  $effect(async () => {
    if (formData.client_id) {
      await fetchAnimals()
    } else {
      animalAutocomplete?.setValue([])
      animalOptions = []
    }
  })

  $effect(() => {
    if (blockOperation) {
      notesElement?.focus()
    } else {
      if (!formData.client_id) clientAutocomplete?.focus()
    }
  })

  function onSlotChange({ slot, vet }) {
    if (slot && vet) motiveElement?.focus()
  }

  function handleTypeChange(e) {
    if (e.target.value === "surgery") {
      formData.nil_by_mouth = 1
    }
    if (selectedCategory && selectedCategory.consultation_type !== e.target.value) {
      applyCategory(null, selectedCategory)
    }
  }

  function handleCategoryChange() {
    applyCategory(selectedCategory, previousCategory)
  }

  // Keep track of the previously applied category so we only replace motives it prefilled
  let previousCategory = null
  // The scheduler duration before a category was applied, restored when the category is cleared
  let durationBeforeCategory = null

  function applyCategory(category, previous) {
    formData.consultation_category_id = category?.id || null
    previousCategory = category

    // Prefill each animal's motive, unless it has been typed by the user
    animals.forEach(animal => {
      if (!animal.motive || animal.motive === previous?.name) animal.motive = category?.name || ""
    })

    if (!category) {
      if (previous) duration = durationBeforeCategory || 10
      durationBeforeCategory = null
      return
    }

    if (!previous) durationBeforeCategory = duration

    // Set the scheduler duration, and if a slot is already selected, extend it from its start time
    // (the scheduler re-checks the slot availability when end_time changes)
    duration = category.duration
    if (formData.start_time) {
      formData.end_time = new Date(new Date(formData.start_time).getTime() + category.duration * 60 * 1000)
    }
  }

  async function fetchCategories() {
    try {
      const response = await get(`/api/v1/clinics/${formData.clinic_id}/consultation_categories`)
      categories = await response.json
    } catch (error) {
      console.error("Error fetching consultation categories", error)
    }
  }

  function handleClose() {
    submitAttempted = false
    open = false
    scheduler?.reset()
    formData.vet_id = null
    formData.start_time = null
    formData.end_time = null
    formData.date = new Date().toISOString().split('T')[0]
    formData.client_id = null
    formData.animal_ids = []
    formData.type = null
    formData.reserved_by = null
    formData.motive = ""
    formData.consultation_category_id = null
    previousCategory = null
    durationBeforeCategory = null
    duration = 10
    animalOptions = []
  }

  async function fetchUsers() {
    try {
      const response = await get(`/api/v1/users?clinic_id=${formData.clinic_id}`)
      users = await response.json
      if (currentUser && clinic?.preselects_consultations_reserved_by) {
        formData.reserved_by = currentUser.id
      }
    } catch (error) {
      console.error("Error fetching users", error)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const intent = e.submitter?.value || "remain"

    if (!validate()) return

    if (intent === "block") return await handleBlockSlot(e)

    submitting = true
    
    const consultation = { ...formData }

    if (consultation.start_time) consultation.start_time = new Date(consultation.start_time).toISOString()
    if (consultation.end_time) consultation.end_time = new Date(consultation.end_time).toISOString()

    consultation.motives = animals.map(animal => {
      return {
        animal_id: animal.id,
        text: animal.motive
      }
    })

    try {
      const response = await post('/api/v1/consultations', { body: { consultation } })
      const { url } = await response.json
      if (!response.ok) return showAlert("Une erreur est survenue lors de la création de la consultation", { type: "error" })
      handleClose()
      showAlert("La consultation a bien été créée", { type: "success" })
      if (intent === "visit") {
        Turbo.visit(url)
      } else {
        // Calendar current page content will automatically update
        if (!calendar) {
          Turbo.visit(window.location.href)
        }
      }
    } catch (error) {
      showAlert("Une erreur est survenue lors de la création de la consultation", { type: "error" })
    } finally {
      submitting = false
    }
  }

  async function handleBlockSlot(e) {
    e.preventDefault()
    try {
      submitting = true
      const { clinic_id, vet_id, date, start_time, end_time, notes } = formData
      const response = await post('/blocked_events', { responseKind: "turbo-stream", body: { blocked_event: { clinic_id, vet_id, date, start_time, end_time, notes } } })
      if (response.ok) {
        handleClose()
      } else {
        showAlert("Une erreur est survenue lors du blocage du créneau", { type: "error" })
      }
    } catch (error) {
      showAlert("Une erreur est survenue lors du blocage du créneau", { type: "error" })
    } finally {
      submitting = false
    }
  }

  function onClientCreate(client) {
    const newItem = { id: client.id, name: client.name }
    clientAutocomplete?.addItems([newItem])
    clientAutocomplete?.setValue(newItem)
    clientAutocomplete?.focus()
    clientFormOpen = false
    clientAdded = true
    setTimeout(() => {
      clientAdded = false
    }, 3500)
  }

  function setActiveAnimal(animal, e) {
    activeAnimal = animal
    motiveElement?.focus()
    motiveElement?.select()
  }

  function handleAnimalTabMouseDown(e) {
    if (document.activeElement === motiveElement) return e.preventDefault()
  }

  function handleMotiveInput(e) {
    if (matchMotives) {
      animals.forEach(animal => {
        animal.motive = e.target.value
      })
    }
  }

  function onCreateBlockedEvents(data) {
    open = false
  }

  // Call fetchUsers on mount
  fetchUsers()

  onMount(async () => {
    await fetchUsers()
    await fetchCategories()
  })

</script>

{#if !calendar}
  <button class="button outline-submit" onclick={() => open = !open}>
    Nouvelle consultation
  </button>
{/if}


<!-- On wide screens (when no slot is preselected) the slot grid sits alongside the consultation fields so it's visible straight away -->
{#snippet typeSwitchFields()}
      {#if calendar}
        <div class="form-item col-span-12">
          <label for="type">Type d'opération</label>
          <SegmentedControl name="type" options={[{ value: false, label: "Consultation" }, { value: true, label: "Blocage de créneau" }]} bind:value={blockOperation} />
        </div>
      {/if}
{/snippet}

{#snippet detailFields()}
      {#if !blockOperation}

        <div class="form-item {pairSpan}" class:invalid={invalid.has("client")}>
        <!-- TODO:! Make disabled clients still show from api/v1/clients but not be selectable -->
         <!-- For this would prob need to adjust autcomplete to test if an item has a disabled propety -->
          <label for="client">Client *</label>
          <!-- <Autocomplete bind:this={clientAutocomplete} url="api/v1/clients" initialUrl="api/v1/clients/popular?clinic_id={clinicId}" valueKey="id" labelKey="name" bind:value={formData.client_id} required  filter={false}/> -->
          <Autocomplete bind:this={clientAutocomplete} url="api/v1/clients" valueKey="id" labelKey="name" bind:value={formData.client_id} required  filter={false}/>
          <button type="button" class="text-left text-xs text-gray-400 mt-2 ml-1 italic hover:underline" onclick={() => clientFormOpen = true} tabindex="-1">Nouveau client?</button>
        </div>

        <div class="form-item {pairSpan}">
          <label for="animals">Animaux</label>
          <AutocompleteMultiple bind:this={animalAutocomplete} bind:items={animalOptions} valueKey="id" labelKey="name" bind:value={formData.animal_ids} onValueChange={setAnimals} />
        </div>

        {#if !twoColumn}
          <FormLabel label="Options consultation" />
        {/if}

        <div class="form-item {typeSpan}">
          <label for="type">Type de consultation *</label>
          <select name="type" id="type" class="select {invalid.has("type") ? "border-red-400! focus:ring-red-400!" : ""}" bind:this={typeElement} bind:value={formData.type} required onchange={handleTypeChange}>
            <option value=""></option>
            <option value="scheduled">Consultation</option>
            <option value="admission">Dépot - hospitalisation</option>
            <option value="surgery">Chirurgie</option>
            <option value="external">Visite rurale</option>
            <option value="equine">Visite équine</option>
          </select>
        </div>

        {#if showCategories}
          <div class="form-item {categorySpan}">
            <label for="consultation_category_id">Catégorie de consultation</label>
            <select name="consultation_category_id" id="consultation_category_id" class="select" bind:value={formData.consultation_category_id} onchange={handleCategoryChange}>
              <option value={null}></option>
              {#each typeCategories as category (category.id)}
                <option value={category.id}>{category.name} ({category.duration} min)</option>
              {/each}
            </select>
          </div>
        {/if}

        <div class="form-item col-span-6">
          <label for="duty_shift">Consultation de garde?</label>
          <SegmentedControl name="duty_shift" options={[{ value: 0, label: "Non" }, { value: 1, label: "Oui" }]} bind:value={formData.duty_shift} />
        </div>

        <div class="form-item col-span-6">
          <label for="nil_by_mouth">A jeun?</label>
          <SegmentedControl name="nil_by_mouth" options={[{ value: 0, label: "Non" }, { value: 1, label: "Oui" }]} bind:value={formData.nil_by_mouth} />
        </div>

      
      {/if}
{/snippet}

{#snippet schedulerField(sideBySide = false)}
  <Scheduler 
    clinic_id={clinicId} 
    bind:vet_id={formData.vet_id} 
    bind:date={formData.date}
    bind:start_time={formData.start_time} 
    bind:end_time={formData.end_time} 
    bind:slotUnavailable
    bind:blockOperation
    bind:duration
    {durationLocked}
    bind:timed
    vetInvalid={invalid.has("slot") && !formData.vet_id}
    maxVetColumns={sideBySide ? 2 : null}
    {sideBySide}
    bind:this={scheduler}
    cancellable={!blockOperation}
    onChange={onSlotChange}
  />
{/snippet}

{#snippet afterFields()}
      {#if blockOperation}
        <div class="form-item col-span-12">
          <label for="notes">Notes</label>
          <textarea name="notes" id="notes" class="textarea {invalid.has("notes") ? "border-red-400! focus:ring-red-400!" : ""}" bind:this={notesElement} bind:value={formData.notes}></textarea>
        </div>

        <div class="form-item col-span-12">
          <button type="button" class="help-text text-left underline cursor-pointer flex items-center gap-1" onclick={() => assignationOpen = true}>
            Bloquer plusieurs créneaux en utilisant ces informations
            <i class="fal fa-external-link-alt"></i>
          </button>
        </div>
        
      {/if}
      
      {#if animals.length > 0}
        <div class="form-item col-span-12">
          <label for="motive">Motif</label>
          
          <div class="flex justify-between items-center">
            
            <!-- The animal tabs -->
            <div class="flex items-center gap-5 mt-4 mb-4">
              {#if matchMotives}
                <div class="text-smaller border-b-2 border-teal-500 text-teal-500 pr-1">Tous les animaux</div>
              {:else}
                {#each animals as animal (animal.id)}
                  {@const active = animal.id === activeAnimal.id}
                  {@const className = active ? "text-gray-900 border-b-2 border-teal-500 text-teal-500 pr-1" : "text-gray-400 border-b-2 border-gray-400 pr-1"}
                  <button type="button" class={`text-smaller ${className}`} 
                    onmousedown={handleAnimalTabMouseDown}
                    onclick={(e) => setActiveAnimal(animal, e)}>
                    {#if animal.icon}
                      <i class={`fal fa-${animal.icon} text-gray-400 text-xs relative top-[-1px]`}></i>
                    {/if}
                    {animal.name}
                  </button>
                {/each}
              {/if}
            </div>

            <!-- A button icon to "lock" the motive text between animals -->
            <button type="button" aria-label="Verrouiller le motif entre animaux" onclick={() => matchMotives = !matchMotives}>
              {#if matchMotives}
                <i class="fal fa-link text-teal-500"></i>
              {:else}
                <i class="fal fa-unlink text-gray-400"></i>
              {/if}
            </button>
            
          </div>
          
          <textarea name="motive" id="motive" placeholder={`Motif pour ${matchMotives ? "tous les animaux" : activeAnimal.name}`} class="textarea" bind:this={motiveElement} bind:value={activeAnimal.motive} oninput={handleMotiveInput}></textarea>
          
        </div>
      {/if}

{/snippet}

<Modal bind:open title={modalTitle} width={twoColumn ? "min(84rem, 90vw)" : "55rem"} onClose={handleClose} closeOnEscape={false} closeOnClickOutside={!clientFormOpen}>

  <form id={formId} onsubmit={handleSubmit} novalidate>

    {#if twoColumn}
      <!-- Each column scrolls on its own, so the slot grid stays in view while filling in the fields -->
      <div class="grid grid-cols-[minmax(0,9fr)_minmax(0,11fr)] h-[max(28rem,calc(100vh-20rem))]">
        <div class="form-items gap-6 content-start overflow-y-auto overflow-x-hidden pr-10 pl-1 -ml-1 pb-1">
          {@render typeSwitchFields()}
          {@render detailFields()}
          {@render afterFields()}
        </div>
        <div class="form-item border-l border-gray-200/80 pl-10 overflow-y-auto overflow-x-hidden">
          {@render schedulerField(true)}
        </div>
      </div>
    {:else}
      <div class="form-items">
        {@render typeSwitchFields()}
        {@render detailFields()}
        <div class="form-item col-span-12">
          {@render schedulerField()}
        </div>
        {@render afterFields()}
      </div>
    {/if}

  </form>

  {#snippet footerButtons()}
    {#if blockOperation}
      <button type="submit" class="button outline-danger" form={formId} disabled={submitting} name="intent" value="block">
        <span>Bloquer créneau</span>
        <i class="fal fa-ban"></i>
      </button>
    
    {:else}
      <!-- Who took the booking: next to the buttons as it's required to validate (and usually preselected) -->
      <div class="order-first mr-auto flex items-center gap-1 text-smaller text-gray-500">
        <label for="reserved_by" class="whitespace-nowrap">Réservé par</label>
        <select name="reserved_by" id="reserved_by" form={formId} bind:this={reservedByElement} bind:value={formData.reserved_by} required
          class="rounded-sm border hover:border-gray-200 focus:border-gray-300 focus:outline-none bg-transparent py-1 pl-1 pr-2 text-smaller cursor-pointer {invalid.has("reserved_by") ? "border-red-400" : "border-transparent"} {formData.reserved_by ? "text-gray-700" : invalid.has("reserved_by") ? "text-red-600" : "text-amber-600"}">
          <option value={null}>à renseigner</option>
          <!-- Show all assistants first -->
          <optgroup label="Assistants"></optgroup>
          {#each assistantUsers as user (user.id)}
            <option value={user.id}>{user.name}</option>
          {/each}
          <optgroup label="Autres utilisateurs"></optgroup>
          {#each otherUsers as user (user.id)}
            <option value={user.id}>{user.name}</option>
          {/each}
        </select>
      </div>

      <button type="submit" class="button submit" form={formId} disabled={submitting} name="intent" value="remain">
        <span>Valider</span>
        <i class="fal fa-check-circle"></i>
      </button>
      
      <button type="submit" class="button outline-submit" form={formId} disabled={submitting} name="intent" value="visit">
        <span>Accéder</span>
        <i class="fal fa-external-link-alt"></i>
      </button>
    {/if}
  {/snippet}
  
</Modal>

<ClientForm bind:open={clientFormOpen} onCreate={onClientCreate} />

<AssignationModal 
  bind:open={assignationOpen} 
  vetId={formData.vet_id}
  clinicId={3}
  startDate={formData.date}
  startTime={formData.start_time ? format(new Date(formData.start_time), "HH:mm") : ""}
  endTime={formData.end_time ? format(new Date(formData.end_time), "HH:mm") : ""}
  days={[getDay(formData.date)]}
  notes={formData.notes}
  onCreate={onCreateBlockedEvents}
  showButton={false}
/>

<style>
  /* Missing required client, highlighted after a submit attempt */
  .invalid :global(.input-container) {
    border-color: var(--color-red-400) !important;
    --tw-ring-color: var(--color-red-400) !important;
  }
</style>
